import { API_METHODS } from '@compass/client/constants/api-methods';
import type {
  CreateEligibilityCheckRequest,
  CreateEligibilityCheckResponse,
  EligibilityCheckService,
  GetCaseRecordEligibilityChecksRequest,
  GetCaseRecordEligibilityChecksResponse,
  GetEligibilityCheckRequest,
  GetEligibilityCheckResponse,
  UpdateEligibilityCheckRequest,
  UpdateEligibilityCheckResponse,
} from '@compass/client/types/eligibility';
import { eligibilityService } from '@services/api';
import { getApiPath, transport } from '@services/api.config';

/**
 * Eligibility-check service.
 *
 * Three of the four methods delegate straight to the generated `eligibilityService`
 * (BV-1874). `getCaseRecordEligibilityChecks` deliberately does NOT — see below.
 */
export const eligibilityChecksService: EligibilityCheckService = {
  async createEligibilityCheck(
    request: CreateEligibilityCheckRequest,
  ): Promise<CreateEligibilityCheckResponse> {
    return eligibilityService.createEligibilityCheck(request);
  },

  async getEligibilityCheck(
    request: GetEligibilityCheckRequest,
  ): Promise<GetEligibilityCheckResponse> {
    return eligibilityService.getEligibilityCheck(request);
  },

  async updateEligibilityCheck(
    request: UpdateEligibilityCheckRequest,
  ): Promise<UpdateEligibilityCheckResponse> {
    return eligibilityService.updateEligibilityCheck(request);
  },

  /**
   * Not delegated to the generated service — the generated one would silently drop every
   * filter.
   *
   * This endpoint's handler is hand-written rather than grpc-gateway generated
   * (`server/http/api/eligibility_check_handlers.go`), and it reads **singular** query
   * parameters: `status`, `payer_id`, `attempt`. The proto fields are plural
   * (`statuses`, `payer_ids`, `attempts`), and the generated service forwards the proto
   * names — which the handler does not look at, so filtering would degrade to "return
   * everything" with no error.
   *
   * The path still comes from `getApiPath`, so it cannot drift from the proto; only the
   * query-string names are mapped by hand. Fixing the proto/handler mismatch is a backend
   * change and is out of scope here.
   */
  async getCaseRecordEligibilityChecks(
    request: GetCaseRecordEligibilityChecksRequest,
  ): Promise<GetCaseRecordEligibilityChecksResponse> {
    const { organization_id, program_id, caserecord_id, ...filters } = request;
    const url = getApiPath(API_METHODS.EligibilityCheckService.GetCaseRecordEligibilityChecks, {
      organization_id,
      program_id,
      caserecord_id,
    });
    const params = buildEligibilityCheckParams(filters);
    const queryString = params.toString();

    return transport.get<GetCaseRecordEligibilityChecksResponse>(
      `${url}${queryString ? `?${queryString}` : ''}`,
    );
  },
};

// Helper function to build query parameters for eligibility checks.
// Names here must match the handler's singular parameters, not the proto's plural fields.
function buildEligibilityCheckParams(
  filters?: Omit<
    GetCaseRecordEligibilityChecksRequest,
    'caserecord_id' | 'organization_id' | 'program_id'
  >,
): URLSearchParams {
  const params = new URLSearchParams();

  addArrayParams(params, 'status', filters?.statuses);
  addArrayParams(params, 'payer_id', filters?.payer_ids);
  addArrayParams(params, 'attempt', filters?.attempts?.map(String));
  addSimpleParam(params, 'limit', filters?.limit?.toString());

  return params;
}

// Helper to add array parameters
function addArrayParams(params: URLSearchParams, key: string, values?: string[]): void {
  values?.forEach(value => params.append(key, value));
}

// Helper to add simple parameters
function addSimpleParam(params: URLSearchParams, key: string, value?: string): void {
  if (value !== undefined) {
    params.append(key, value);
  }
}
